import { useNavigate } from "react-router";
import FormTemplate from "../../templates/FormTemplate";
import { BrandingService, PrintService } from "../../../services/api";
import { useTranslation } from "react-i18next";
import { useEffect, useState } from "react";
import { useGlobalContext } from "../../../stores/Global";
import { Grid, Stack } from "@mui/material";
import FormInput from "../../molecules/FormInput";
import ImageInput from "../../molecules/ImageInput";
import FieldLabel from "../../atoms/FieldLabel";
import SectionTitle from "../../atoms/SectionTitle";
import { useMutation } from "@tanstack/react-query";
import { useBrandingStore } from "../../../stores/Branding";
import FaviconInput from "../../molecules/FaviconInput";

export default function BrandingSettingsPage() {
  const { context } = useGlobalContext();
  const { t } = useTranslation("Branding");
  const currentBranding = useBrandingStore((s) => s.branding);
  const setBranding = useBrandingStore((s) => s.setBranding);
  const navigate = useNavigate();

  const [contactAddress, setContactAddress] = useState("");
  const [contactEmail, setContactEmail] = useState("");
  const [contactMapEmbed, setContactMapEmbed] = useState("");
  const [contactPhone, setContactPhone] = useState("");
  const [contactWhatsapp, setContactWhatsapp] = useState("");
  const [contactWhatsappLabel, setContactWhatsappLabel] = useState("");

  const [facebookLabel, setFacebookLabel] = useState("");
  const [facebook, setFacebook] = useState("");
  const [instagramLabel, setInstagramLabel] = useState("");
  const [instagram, setInstagram] = useState("");
  const [linkedinLabel, setLinkedinLabel] = useState("");
  const [linkedin, setLinkedin] = useState("");
  const [website, setWebsite] = useState("");
  const [websiteLabel, setWebsiteLabel] = useState("");
  const [xLabel, setXLabel] = useState("");
  const [x, setX] = useState("");
  const [youtubeLabel, setYoutubeLabel] = useState("");
  const [youtube, setYoutube] = useState("");

  const [favicon, setFavicon] = useState("");
  const [logoImage, setLogoImage] = useState("");
  const [logoLink, setLogoLink] = useState("");
  const [name, setName] = useState("");
  const [shortname, setShortname] = useState("");
  const [description, setDescription] = useState("");
  const [title, setTitle] = useState("");
  const [whatWeDo, setWhatWeDo] = useState("");
  const [themeColor, setThemeColor] = useState("#42A5F5");
  const [domain, setDomain] = useState("");

  const { mutate: loadBranding, isPending: isBrandingLoading } = useMutation({
    mutationFn: async () => {
      const res = await BrandingService.getBranding();
      return res?.data?.data;
    },
    onSuccess: (result) => {
      const data = result || currentBranding;
      setContactAddress(data.contact.address);
      setContactEmail(data.contact.email);
      setContactMapEmbed(data.contact.mapEmbed);
      setContactPhone(data.contact.phone);
      setContactWhatsapp(data.contact.whatsapp);
      setContactWhatsappLabel(data.contact.whatsappLabel);

      setFacebookLabel(data.links.facebook.label);
      setFacebook(data.links.facebook.link);
      setInstagramLabel(data.links.instagram.label);
      setInstagram(data.links.instagram.link);
      setLinkedinLabel(data.links.linkedin.label);
      setLinkedin(data.links.linkedin.link);
      setWebsiteLabel(data.links.website.label);
      setWebsite(data.links.website.link);
      setXLabel(data.links.x.label);
      setX(data.links.x.link);
      setYoutubeLabel(data.links.youtube.label);
      setYoutube(data.links.youtube.link);

      setLogoImage(data.logo.image);
      setLogoLink(data.logo.link);

      setFavicon(data.favicon);
      setName(data.name);
      setShortname(data.shortname);
      setThemeColor(data.themeColor);
      setDomain(data.domain);
      setDescription(data.description);
      setTitle(data.title);
      setWhatWeDo(data.whatWeDo);
    },
  });

  const handleClose = () => {
    navigate(-1);
  };

  const handleSubmit = async () => {
    const res = await BrandingService.setBranding({
      branding: {
        contact: {
          address: contactAddress,
          email: contactEmail,
          mapEmbed: contactMapEmbed,
          phone: contactPhone,
          whatsapp: contactWhatsapp,
          whatsappLabel: contactWhatsappLabel,
        },
        domain,
        links: {
          facebook: { label: facebookLabel, link: facebook },
          instagram: { label: instagramLabel, link: instagram },
          linkedin: { label: linkedinLabel, link: linkedin },
          website: { label: websiteLabel, link: website },
          x: { label: xLabel, link: x },
          youtube: { label: youtubeLabel, link: youtube },
        },
        logo: {
          image: logoImage,
          link: logoLink,
        },
        name,
        shortname,
        themeColor,
        favicon,
        description,
        title,
        whatWeDo,
      },
    });
    if (res?.data?.data) {
      setBranding(res.data.data);
    }
  };

  useEffect(() => {
    if (!context.user) return;
    loadBranding();
  }, [context]);

  const loading = !context.user || isBrandingLoading;

  return (
    <FormTemplate
      title={t("BrandingSettings")}
      disableCancelButton
      disableEnter
      onSubmit={handleSubmit}
      onCancel={handleClose}
      noForm
      confirm
      loading={loading}
    >
      <Grid container rowGap={2} alignItems={"center"} sx={{ height: 1 }}>
        <Grid size={{ xs: 5, md: 3 }}>
          <FieldLabel children={t("Domain")} required />
        </Grid>
        <Grid size={{ xs: 7, md: 9 }}>
          <FormInput
            loading={loading}
            label={""}
            type="string"
            field="domain"
            value={domain}
            onChange={(e) => setDomain(e as string)}
            required
          />
        </Grid>

        <Grid size={12}>
          <FaviconInput
            label={t("Favicon")}
            field={"favicon"}
            value={favicon}
            onChange={(value) => setFavicon(value)}
            loading={loading}
          />
        </Grid>

        <Grid size={12}>
          <ImageInput
            label={t("Logo")}
            field={"logo"}
            value={logoImage}
            width="100%"
            onChange={(value) => setLogoImage(value)}
            loading={loading}
          />
        </Grid>

        <Grid size={{ xs: 12 }}>
          <FormInput
            label={t("LogoLink")}
            field={"logoLink"}
            value={logoLink}
            type="string"
            onChange={(v) => setLogoLink(v as string)}
            loading={loading}
          />
        </Grid>

        {/* Branding */}
        <Grid size={{ xs: 12 }}>
          <SectionTitle>{t("Branding")}</SectionTitle>
        </Grid>

        <Grid container size={12} spacing={2}>
          <Grid size={6}>
            <FormInput
              loading={loading}
              label={t("Name")}
              type="string"
              field={"name"}
              value={name}
              onChange={(e) => setName(e as string)}
            />
          </Grid>
          <Grid size={6}>
            <FormInput
              loading={loading}
              label={t("Shortname")}
              type="string"
              field={"shortname"}
              value={shortname}
              onChange={(e) => setShortname(e as string)}
            />
          </Grid>
          <Grid size={12}>
            <FormInput
              loading={loading}
              label={t("Title")}
              type="string"
              field={"title"}
              value={title}
              onChange={(e) => setTitle(e as string)}
            />
          </Grid>
          <Grid size={12}>
            <FormInput
              loading={loading}
              label={t("Description")}
              type="string"
              field={"description"}
              value={description}
              onChange={(e) => setDescription(e as string)}
            />
          </Grid>
          <Grid size={12}>
            <FormInput
              loading={loading}
              label={t("WhatWeDo")}
              type="string"
              field={"whatWeDo"}
              value={whatWeDo}
              multiline
              minRows={3}
              maxRows={4}
              onChange={(e) => setWhatWeDo(e as string)}
            />
          </Grid>
          <Grid size={12}>
            <FormInput
              loading={loading}
              label={t("ThemeColor")}
              type="color"
              field={"themeColor"}
              value={themeColor}
              onChange={(e) => setThemeColor(e as string)}
            />
          </Grid>
        </Grid>

        {/* Contacts */}
        <Grid size={{ xs: 12 }}>
          <SectionTitle>{t("Contacts")}</SectionTitle>
        </Grid>

        <Grid container size={12} spacing={2}>
          <Grid size={6}>
            <FormInput
              loading={loading}
              label={t("Address")}
              type="string"
              field={"contactAddress"}
              value={contactAddress}
              onChange={(e) => setContactAddress(e as string)}
            />
          </Grid>
          <Grid size={6}>
            <FormInput
              loading={loading}
              label={t("Email")}
              type="string"
              field={"contactEmail"}
              value={contactEmail}
              onChange={(e) => setContactEmail(e as string)}
            />
          </Grid>
          <Grid size={6}>
            <FormInput
              loading={loading}
              label={t("MapEmbed")}
              type="string"
              field={"contactMapEmbed"}
              value={contactMapEmbed}
              onChange={(e) => setContactMapEmbed(e as string)}
            />
          </Grid>
          <Grid size={6}>
            <FormInput
              loading={loading}
              label={t("Phone")}
              type="string"
              field={"contactPhone"}
              value={contactPhone}
              onChange={(e) => setContactPhone(e as string)}
            />
          </Grid>
          <Grid size={6}>
            <FormInput
              loading={loading}
              label={t("WhatsappLabel")}
              type="string"
              field={"contactWhatsappLabel"}
              value={contactWhatsappLabel}
              onChange={(e) => setContactWhatsappLabel(e as string)}
            />
          </Grid>
          <Grid size={6}>
            <FormInput
              loading={loading}
              label={t("WhatsappNumber")}
              type="string"
              field={"contactWhatsapp"}
              value={contactWhatsapp}
              onChange={(e) => setContactWhatsapp(e as string)}
            />
          </Grid>
        </Grid>

        {/* Links */}
        <Grid size={{ xs: 12 }}>
          <SectionTitle>{t("Links")}</SectionTitle>
        </Grid>

        <Grid container size={12} spacing={2}>
          <Grid size={6}>
            <FormInput
              loading={loading}
              label={t("WebsiteLabel")}
              type="string"
              field={"websiteLabel"}
              value={websiteLabel}
              onChange={(e) => setWebsiteLabel(e as string)}
            />
          </Grid>
          <Grid size={6}>
            <FormInput
              loading={loading}
              label={t("WebsiteLink")}
              type="string"
              field={"website"}
              value={website}
              onChange={(e) => setWebsite(e as string)}
            />
          </Grid>
          <Grid size={6}>
            <FormInput
              loading={loading}
              label={t("FacebookLabel")}
              type="string"
              field={"facebookLabel"}
              value={facebookLabel}
              onChange={(e) => setFacebookLabel(e as string)}
            />
          </Grid>
          <Grid size={6}>
            <FormInput
              loading={loading}
              label={t("FacebookLink")}
              type="string"
              field={"facebook"}
              value={facebook}
              onChange={(e) => setFacebook(e as string)}
            />
          </Grid>
          <Grid size={6}>
            <FormInput
              loading={loading}
              label={t("InstagramLabel")}
              type="string"
              field={"instagramLabel"}
              value={instagramLabel}
              onChange={(e) => setInstagramLabel(e as string)}
            />
          </Grid>
          <Grid size={6}>
            <FormInput
              loading={loading}
              label={t("InstagramLink")}
              type="string"
              field={"instagram"}
              value={instagram}
              onChange={(e) => setInstagram(e as string)}
            />
          </Grid>
          <Grid size={6}>
            <FormInput
              loading={loading}
              label={t("LinkedinLabel")}
              type="string"
              field={"linkedinLabel"}
              value={linkedinLabel}
              onChange={(e) => setLinkedinLabel(e as string)}
            />
          </Grid>
          <Grid size={6}>
            <FormInput
              loading={loading}
              label={t("LinkedinLink")}
              type="string"
              field={"linkedin"}
              value={linkedin}
              onChange={(e) => setLinkedin(e as string)}
            />
          </Grid>
          <Grid size={6}>
            <FormInput
              loading={loading}
              label={t("XLabel")}
              type="string"
              field={"xLabel"}
              value={xLabel}
              onChange={(e) => setXLabel(e as string)}
            />
          </Grid>
          <Grid size={6}>
            <FormInput
              loading={loading}
              label={t("XLink")}
              type="string"
              field={"x"}
              value={x}
              onChange={(e) => setX(e as string)}
            />
          </Grid>
          <Grid size={6}>
            <FormInput
              loading={loading}
              label={t("YoutubeLabel")}
              type="string"
              field={"youtubeLabel"}
              value={youtubeLabel}
              onChange={(e) => setYoutubeLabel(e as string)}
            />
          </Grid>
          <Grid size={6}>
            <FormInput
              loading={loading}
              label={t("YoutubeLink")}
              type="string"
              field={"youtube"}
              value={youtube}
              onChange={(e) => setYoutube(e as string)}
            />
          </Grid>
        </Grid>
      </Grid>
    </FormTemplate>
  );
}
